import React, { useState, useEffect } from 'react';
import { MdClose, MdPerson, MdEdit, MdEmail, MdPhone, MdLocationOn, MdBusiness } from 'react-icons/md';
import axios from 'axios';
import SearchableDropdown from './SearchableDropdown';

const EditRevenueOfficerModal = ({ isOpen, onClose, showPopup, fetchData, API_BASE_URL, editRevenueOfficer, zones, mmdaOptions }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    phone: '',
    zone_id: null,
    mmda_id: null,
    status: 'active'
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (editRevenueOfficer) {
      setFormData({
        first_name: editRevenueOfficer.first_name || '',
        middle_name: editRevenueOfficer.middle_name || '',
        last_name: editRevenueOfficer.last_name || '',
        email: editRevenueOfficer.email || '',
        phone: editRevenueOfficer.phone || '',
        zone_id: editRevenueOfficer.zone_id || null,
        mmda_id: editRevenueOfficer.mmda_id || null,
        status: editRevenueOfficer.status || 'active'
      });
      setErrors({});
    }
  }, [editRevenueOfficer, zones, mmdaOptions]);

  if (!isOpen) {
    return null;
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData.mmda_id) {
      newErrors.mmda_id = 'MMDA is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const config = {
        headers: {
          'Authorization': 'Basic ' + btoa('Techland:Techland@24'),
          'Content-Type': 'application/json',
        },
      };

      const updatedRevenueOfficerData = {
        first_name: formData.first_name,
        middle_name: formData.middle_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        zone_id: formData.zone_id,
        mmda_id: formData.mmda_id,
        status: formData.status
      };

      const officerId = editRevenueOfficer.officer_id || editRevenueOfficer.revenue_officer_id;
      await axios.put(`${API_BASE_URL}/revenue-officers/${officerId}`, updatedRevenueOfficerData, config);

      showPopup('Revenue Officer updated successfully!', 'success');
      fetchData();
      handleClose();
    } catch (err) {
      console.error('Failed to update revenue officer:', err);
      showPopup('Failed to update revenue officer. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      first_name: '',
      middle_name: '',
      last_name: '',
      email: '',
      phone: '',
      zone_id: null,
      mmda_id: null,
      status: 'active'
    });
    setErrors({});
    setIsSubmitting(false);
    onClose();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(4px)'
    }} onClick={handleClose}>
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: 16,
        padding: 0,
        width: '90%',
        maxWidth: 600,
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        transform: 'scale(1)',
        transition: 'all 0.3s ease'
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '24px 32px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <MdEdit size={28} />
            <h2 style={{ margin: 0, fontSize: 24, fontWeight: '600' }}>Edit Revenue Officer</h2>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: 8,
              padding: 8,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
          >
            <MdClose size={20} color="white" />
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} style={{ padding: '32px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

            {/* First Name Field */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: '600',
                color: '#374151',
                fontSize: 14
              }}>
                <MdPerson size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                First Name *
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                placeholder="Enter first name"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: errors.first_name ? '2px solid #ef4444' : '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  backgroundColor: '#fafafa'
                }}
                onFocus={e => {
                  if (!errors.first_name) {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.backgroundColor = '#ffffff';
                  }
                }}
                onBlur={e => {
                  if (!errors.first_name) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.backgroundColor = '#fafafa';
                  }
                }}
              />
              {errors.first_name && (
                <p style={{ color: '#ef4444', fontSize: 12, margin: '4px 0 0 0' }}>
                  {errors.first_name}
                </p>
              )}
            </div>

            {/* Last Name Field */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: '600',
                color: '#374151',
                fontSize: 14
              }}>
                <MdPerson size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                Last Name *
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                placeholder="Enter last name"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: errors.last_name ? '2px solid #ef4444' : '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  backgroundColor: '#fafafa'
                }}
                onFocus={e => {
                  if (!errors.last_name) {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.backgroundColor = '#ffffff';
                  }
                }}
                onBlur={e => {
                  if (!errors.last_name) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.backgroundColor = '#fafafa';
                  }
                }}
              />
              {errors.last_name && (
                <p style={{ color: '#ef4444', fontSize: 12, margin: '4px 0 0 0' }}>
                  {errors.last_name}
                </p>
              )}
            </div>

            {/* Middle Name Field */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: '600',
                color: '#374151',
                fontSize: 14
              }}>
                <MdPerson size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                Middle Name
              </label>
              <input
                type="text"
                name="middle_name"
                value={formData.middle_name}
                onChange={handleInputChange}
                placeholder="Enter middle name (optional)"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  backgroundColor: '#fafafa'
                }}
                onFocus={e => {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }}
                onBlur={e => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.backgroundColor = '#fafafa';
                }}
              />
            </div>

            {/* Email Field */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: '600',
                color: '#374151',
                fontSize: 14
              }}>
                <MdEmail size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter email address"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: errors.email ? '2px solid #ef4444' : '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  backgroundColor: '#fafafa'
                }}
                onFocus={e => {
                  if (!errors.email) {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.backgroundColor = '#ffffff';
                  }
                }}
                onBlur={e => {
                  if (!errors.email) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.backgroundColor = '#fafafa';
                  }
                }}
              />
              {errors.email && (
                <p style={{ color: '#ef4444', fontSize: 12, margin: '4px 0 0 0' }}>
                  {errors.email}
                </p>
              )}
            </div>

            {/* Phone Field */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: '600',
                color: '#374151',
                fontSize: 14
              }}>
                <MdPhone size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="Enter phone number"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: errors.phone ? '2px solid #ef4444' : '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  backgroundColor: '#fafafa'
                }}
                onFocus={e => {
                  if (!errors.phone) {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.backgroundColor = '#ffffff';
                  }
                }}
                onBlur={e => {
                  if (!errors.phone) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.backgroundColor = '#fafafa';
                  }
                }}
              />
              {errors.phone && (
                <p style={{ color: '#ef4444', fontSize: 12, margin: '4px 0 0 0' }}>
                  {errors.phone}
                </p>
              )}
            </div>

            {/* Zone Field */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: '600',
                color: '#374151',
                fontSize: 14
              }}>
                <MdLocationOn size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                Zone
              </label>
              <div style={{ position: 'relative' }}>
                <SearchableDropdown
                  options={(zones || []).map((zone) => ({
                    value: zone.zone_id,
                    label: zone.zone_name,
                  }))}
                  value={formData.zone_id}
                  onChange={(value) => setFormData(prev => ({ ...prev, zone_id: value }))}
                  placeholder="Select Zone"
                />
              </div>
            </div>

            {/* MMDA Field */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: '600',
                color: '#374151',
                fontSize: 14
              }}>
                <MdBusiness size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                MMDA *
              </label>
              <div style={{ position: 'relative' }}>
                <SearchableDropdown
                  options={(mmdaOptions || []).map((mmda) => ({
                    value: mmda.mmda_id || mmda.value,
                    label: mmda.mmda_name || mmda.label || mmda.name,
                  }))}
                  value={formData.mmda_id}
                  onChange={(value) => setFormData(prev => ({ ...prev, mmda_id: value }))}
                  placeholder="Select MMDA"
                />
              </div>
              {errors.mmda_id && (
                <p style={{ color: '#ef4444', fontSize: 12, margin: '4px 0 0 0' }}>
                  {errors.mmda_id}
                </p>
              )}
            </div>

            {/* Status Field */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: '600',
                color: '#374151',
                fontSize: 14
              }}>
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  backgroundColor: '#fafafa',
                  cursor: 'pointer'
                }}
                onFocus={e => {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }}
                onBlur={e => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.backgroundColor = '#fafafa';
                }}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* Created By Field */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: '600',
                color: '#374151',
                fontSize: 14
              }}>
                Created By
              </label>
              <div style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: 8,
                fontSize: 14,
                color: '#6b7280',
                backgroundColor: '#f9fafb',
                display: 'flex',
                alignItems: 'center'
              }}>
                {editRevenueOfficer?.created_by_name || 'N/A'}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12,
            marginTop: 32,
            paddingTop: 24,
            borderTop: '1px solid #e5e7eb'
          }}>
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              style={{
                padding: '12px 24px',
                border: '2px solid #e5e7eb',
                borderRadius: 8,
                backgroundColor: '#ffffff',
                color: '#374151',
                fontSize: 14,
                fontWeight: '600',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: isSubmitting ? 0.6 : 1
              }}
              onMouseEnter={e => {
                if (!isSubmitting) {
                  e.currentTarget.style.borderColor = '#d1d5db';
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                }
              }}
              onMouseLeave={e => {
                if (!isSubmitting) {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                padding: '12px 24px',
                border: 'none',
                borderRadius: 8,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontSize: 14,
                fontWeight: '600',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: isSubmitting ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
              onMouseEnter={e => {
                if (!isSubmitting) {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                }
              }}
              onMouseLeave={e => {
                if (!isSubmitting) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              <MdEdit size={16} />
              {isSubmitting ? 'Updating...' : 'Update Revenue Officer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRevenueOfficerModal;
